import { jwtDecode } from "jwt-decode";
import { getUserLocale } from "./commonStrings";
import { JwtPayload } from "./globalTypes";

export function isIOSBrowser(): boolean {
	return (
		navigator.userAgent.indexOf("iPad") > -1 ||
		navigator.userAgent.indexOf("iPhone") > -1 ||
		navigator.userAgent.indexOf("iPod") > -1
	);
}

export function prepareDownloadWindow(): Window | null {
	if (isIOSBrowser()) {
		return window.open();
	} else return null;
}

export function downloadArrayBuffer(
	pBuffer: ArrayBuffer,
	pFilename: string,
	pDownloadWindow: Window | null,
	pMimeType = "application/octet-stream",
): void {
	let tBlob = new Blob([pBuffer], { type: pMimeType });
	downloadBlob(tBlob, pFilename, pDownloadWindow);
}

export async function downloadBlob(
	pBlob: Blob,
	pFilename: string,
	pDownloadWindow: Window | null,
): Promise<void> {
	if (isIOSBrowser() && pDownloadWindow != null) {
		//IOS
		let tUrl = URL.createObjectURL(pBlob);
		pDownloadWindow.onload = () => {
			URL.revokeObjectURL(tUrl);
		};
		pDownloadWindow.location.href = tUrl;
	} else if ((navigator as any).msSaveBlob != null) {
		//IE
		(navigator as any).msSaveBlob(pBlob, pFilename);
	} else {
		let tLink = document.createElement("a");
		tLink.style.display = "none";
		document.body.appendChild(tLink);

		let tUrl = URL.createObjectURL(pBlob);
		tLink.href = tUrl;
		tLink.download = pFilename;
		tLink.click();

		URL.revokeObjectURL(tUrl);
		document.body.removeChild(tLink);
	}
}

export function arrayBufferToBase64(buffer: ArrayBuffer) {
	var binary = "";
	var bytes = new Uint8Array(buffer);
	var len = bytes.byteLength;
	for (var i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}

export function base64ToArrayBuffer(base64: string) {
	var binaryString = atob(base64);
	var bytes = new Uint8Array(binaryString.length);
	for (var i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes.buffer;
}

export const toMinutes = (
	n: number,
	unit: "Milliseconds" | "Minutes" | "Seconds",
): number => {
	let m = 0;
	switch (unit) {
		case "Milliseconds":
			m = (0.001 * n) / 60;
			break;
		case "Minutes":
			m = n;
			break;
		case "Seconds":
			m = n / 60;
			break;
	}
	return Math.round((m + Number.EPSILON) * 100) / 100;
};

export const currencyFormatter = (currency: string | undefined) =>
	currency
		? new Intl.NumberFormat(getUserLocale(), {
				style: "currency",
				currency: currency,
			})
		: null;

/**
 * Compares two timestamps and returns `true` if the difference between them is less than 5 minutes.
 *
 * @param timestamp1 - The first timestamp in milliseconds.
 * @param timestamp2 - The second timestamp in milliseconds.
 * @returns `true` if the difference is less than 5 minutes, otherwise `false`.
 */
export function isLessThanFiveMinutes(
	timestamp1: number,
	timestamp2: number,
): boolean {
	// Calculate the absolute difference in milliseconds
	const differenceInMillis = Math.abs(timestamp1 - timestamp2);

	// Convert the difference to minutes
	const differenceInMinutes = differenceInMillis / (1000 * 60);

	// Return true if the difference is less than 5 minutes
	return differenceInMinutes < 5;
}

/**
 * decode jwt token
 * @param token: string | null | undefined
 * @returns JwtPayload | null
 */
export function decodeJwtToken(token?: string | null): JwtPayload | null {
	if (!token) {
		console.warn('No token provided to decode');
		return null;
	}

	try {
		// Check if the token has the correct format (at least has 2 dots)
		if (!token.includes('.') || token.split('.').length !== 3) {
			console.error('Invalid token format');
			return null;
		}

		return jwtDecode<JwtPayload>(token);
	} catch (error) {
		console.error('Failed to decode JWT token:', error);
		console.error('Token:', token);
		return null;
	}
}

